/**
 * The one definition of what a system todo's `title_data` may hold. The Deno writer, both app
 * renderers and the drift tests read it, so adding a kind or a fact is one edit here (see the
 * `versioned-payloads` skill). Dependency-free and extension-qualified so Deno imports it by path,
 * like `packages/sie4-node`.
 */

/** The `title_data` version writers produce; a payload without `v` is version 1. */
export const TODO_TITLE_DATA_VERSION = 1;

type Fact = (value: unknown) => boolean;

const text: Fact = (value) => typeof value === "string" && value !== "";
const positiveNumber: Fact = (value) => typeof value === "number" && Number.isFinite(value) && value > 0;
const isoDate: Fact = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

interface KindDefinition {
  /** Facts the wording needs; a payload missing or malforming one reads as the generic sentence. */
  required: Record<string, Fact>;
  /** Facts that refine the wording when present. */
  optional?: Record<string, Fact>;
}

export const TODO_TITLE_KINDS = {
  upload_receipt: {
    required: { amount: positiveNumber, currency: text, date: isoDate },
    optional: { vendor: text, description: text },
  },
  classify_document: { required: {} },
  reconnect_tax_account: { required: { authority: text, accountLabel: text } },
  pay_payable: { required: { taxType: text } },
} as const satisfies Record<string, KindDefinition>;

export type TodoTitleKind = keyof typeof TODO_TITLE_KINDS;

export function isTodoTitleKind(kind: unknown): kind is TodoTitleKind {
  return typeof kind === "string" && Object.hasOwn(TODO_TITLE_KINDS, kind);
}

export type TodoTitleFault = "unknown_kind" | "missing_facts";

/** Why a payload cannot be worded, or null when its kind is known and its facts are well formed. */
export function todoTitleFault(data: object): TodoTitleFault | null {
  const facts = data as Record<string, unknown>;
  if (!isTodoTitleKind(facts.kind)) return "unknown_kind";
  const definition: KindDefinition = TODO_TITLE_KINDS[facts.kind];
  for (const [name, valid] of Object.entries(definition.required)) {
    if (!valid(facts[name])) return "missing_facts";
  }
  for (const [name, valid] of Object.entries(definition.optional ?? {})) {
    if (facts[name] !== undefined && !valid(facts[name])) return "missing_facts";
  }
  return null;
}

/** For writers: refuse to store a payload no reader could word. */
export function assertTodoTitleData(data: object): void {
  const fault = todoTitleFault(data);
  if (fault) throw new Error(`todo title_data rejected (${fault}) for kind ${String((data as { kind?: unknown }).kind)}`);
}
